import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { IdCard, Trash2, Upload, X } from "lucide-react";
import { useForm } from "react-hook-form";

import {
  ICamposVariaveis,
  IEditModeloModalProps,
} from "@pages/admin/gerenciar-modelos-tecnicos/components/edit-model-modal/interface";
// typings
import {
  FormDataEditModel,
  schemaModelo,
} from "@pages/admin/gerenciar-modelos-tecnicos/components/edit-model-modal/schema/edit-model";

import { useAtualizarModelo } from "@/hooks/modelos/atualizar-modelo";
import { useSincronizarCamposVariaveisModelo } from "@/hooks/modelos/sincronizar-campos-variaveis-modelo";
// hooks
import { useListarProdutosAtivosSelecao } from "@/hooks/produtos/listar-produtos-ativos-selecao";
import { useBuscarTecnologias } from "@/hooks/tecnlogias/buscar-tecnologias";

// components
import { Button } from "@/presentation/components/button/button";
import { SpinnerSimple } from "@/presentation/components/spinner-simple/spinner-simple";

// utils
import { capitalize } from "@/utils/format-capitalize";
import { normalizarTextoComparacao } from "@/utils/normalizar-texto-comparacao";
import { notifyAlert } from "@/utils/notify-messages";

const EditModeloModal = ({ handleToggleModal, modelo }: IEditModeloModalProps) => {
  const location = useLocation();
  const { userCliente } = location.state || {};

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormDataEditModel>({
    resolver: zodResolver(schemaModelo),
    defaultValues: {
      cliente: modelo.cliente?.nome,
      produto: modelo.produto?.nome,
      nomeModelo: modelo.nome_modelo ?? "N/A",
      posicionamento: capitalize(modelo.posicionamento ?? "N/A") as "Horizontal" | "Vertical",
      tipoFuro: capitalize(modelo.tipo_furo ?? "N/A") as string,
      temFuro: Boolean(modelo.tem_furo),
      temCargaFotos: Boolean(modelo.tem_carga_foto),
      temDadosVariaveis: Boolean(modelo.tem_dados_variaveis),
      isProvisorio: Boolean(modelo.is_provisorio),
      campoChave: modelo.campo_chave || "N/A",
      observacoes: modelo.observacoes || "N/A",
    },
  });

  const { atualizarModeloAsync, isLoading } = useAtualizarModelo();
  const { sincronizarCamposVariaveisModeloAsync, isLoading: isLoadingSyncCampos } =
    useSincronizarCamposVariaveisModelo();

  const { tecnologias, isLoadingTecnologias } = useBuscarTecnologias({ somenteAtivos: true });

  const { produtos } = useListarProdutosAtivosSelecao();

  const produtosParaSelecao = useMemo(() => {
    const produtoVinculado = modelo?.produto;
    if (!produtoVinculado) {
      return produtos;
    }

    const produtoJaListado = produtos.some((item) => item.id === produtoVinculado.id);
    if (produtoJaListado || Boolean(produtoVinculado.ativo)) {
      return produtos;
    }

    return [
      ...produtos,
      {
        id: produtoVinculado.id,
        nome: produtoVinculado.nome,
        valor: produtoVinculado.valor,
        valor_creditos: produtoVinculado.valor_creditos,
        tecnologia: produtoVinculado.tecnologia,
        estoque_maximo: produtoVinculado.estoque_maximo,
        estoque_minimo: produtoVinculado.estoque_minimo,
        estoque_atual: produtoVinculado.estoque_atual,
        ativo: false,
        created_at: produtoVinculado.created_at,
        updated_at: produtoVinculado.updated_at,
        deleted_at: null,
      },
    ];
  }, [modelo?.produto, produtos]);

  const tecnologiasParaSelecao = useMemo(() => {
    const tecnologiaVinculada = modelo?.tecnologia;
    if (!tecnologiaVinculada) {
      return tecnologias;
    }

    const tecnologiaJaListada = tecnologias.some((item) => item.id === tecnologiaVinculada.id);
    if (tecnologiaJaListada || Boolean(tecnologiaVinculada.ativo)) {
      return tecnologias;
    }

    return [
      ...tecnologias,
      {
        id: tecnologiaVinculada.id,
        nome: tecnologiaVinculada.nome,
        descricao: tecnologiaVinculada.descricao,
        ativo: false,
        created_at: tecnologiaVinculada.created_at,
        updated_at: tecnologiaVinculada.updated_at,
        deleted_at: null,
      },
    ];
  }, [modelo?.tecnologia, tecnologias]);

  const [frontFile, setFrontFile] = useState<string | null>(modelo.foto_frente_path || null);
  const [backFile, setBackFile] = useState<string | null>(modelo.foto_verso_path || null);

  const [fileFrente, setFileFrente] = useState<File | null>(null);
  const [fileVerso, setFileVerso] = useState<File | null>(null);

  const [isChecked, setIsChecked] = useState(modelo.tem_furo ?? false);
  const [imagemAmpliada, setImagemAmpliada] = useState<string | null>(null);

  const [camposVariaveis, setCamposVariaveis] = useState<ICamposVariaveis[]>([]);
  const [campo, setCampo] = useState("");
  const [campoObrigatorio, setCampoObrigatorio] = useState(false);
  const [messageError, setMessageError] = useState({
    error: false,
    message: "",
  });

  const [tecnologiasSelecionadas, setTecnologiasSelecionadas] = useState<number[]>([]);

  const [tipoEntregaSelecionado, setTipoEntregaSelecionado] = useState<number>(0);

  // Extrai tecnologias únicas com id + nome
  const tecnologiasUnicas = useMemo(() => {
    const mapa = new Map<number, { id: number; nome: string }>();
    tecnologiasParaSelecao.forEach((item) => {
      if (!mapa.has(item.id)) {
        mapa.set(item.id, { id: item.id, nome: item.nome });
      }
    });
    return Array.from(mapa.values());
  }, [tecnologiasParaSelecao]);

  const handleFrontFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const fileFront = event.target.files[0];

      setFileFrente(fileFront);
      const imageUrl = URL.createObjectURL(fileFront);
      setFrontFile(imageUrl);
    }
  };

  const handleBackFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const fileBack = event.target.files[0];

      setFileVerso(fileBack);
      const imageUrl = URL.createObjectURL(fileBack);
      setBackFile(imageUrl);
    }
  };

  const handleAddCamposVariaveis = useCallback(() => {
    if (!campo) {
      setMessageError({
        error: true,
        message: "Preencha o nome do campo variável",
      });
      return;
    }
    setCamposVariaveis([
      ...camposVariaveis,
      {
        rowKey: crypto.randomUUID(),
        nome: campo,
        obrigatorio: campoObrigatorio,
        ordem: camposVariaveis.length + 1,
      },
    ]);
    setMessageError({
      error: false,
      message: "",
    });

    setCampo("");
  }, [campo, camposVariaveis, campoObrigatorio]);

  const handleRemoveCamposVariaveis = (rowKey: string) => {
    setCamposVariaveis((prev) => prev.filter((item) => item.rowKey !== rowKey));
  };

  const onSubmit = async (data: FormDataEditModel) => {
    const produtoSelecionado = produtosParaSelecao.find((item) => item.nome === data.produto);

    if (!produtoSelecionado) {
      notifyAlert("Você deve selecionar um produto!");
      return;
    }

    const tecnologiaSelecionada = tecnologiasParaSelecao.find(
      (item) => item.id === tecnologiasSelecionadas[0]
    );

    if (!tecnologiaSelecionada) {
      notifyAlert("Você deve selecionar uma tecnologia!");
      return;
    }

    const atualizandoModelo = {
      user_id: userCliente,
      produto_id: produtoSelecionado.id,
      tecnologia_id: tecnologiaSelecionada.id,
      nome_modelo: data?.nomeModelo,
      tipo_entrega_id: tipoEntregaSelecionado,
      tipo_furo: data?.tipoFuro?.toLowerCase() ?? null,
      posicionamento: data.posicionamento.toLowerCase(),
      tem_furo: data?.temFuro,
      tem_carga_foto: data?.temCargaFotos ? 1 : 0,
      tem_dados_variaveis: data.temDadosVariaveis ? 1 : 0,
      is_provisorio: data.isProvisorio,
      campo_chave: data?.campoChave,
      foto_frente: fileFrente,
      foto_verso: fileVerso,
      observacoes: data.observacoes,
    };

    try {
      await atualizarModeloAsync({
        modelo_id: modelo?.id,
        payload: atualizandoModelo as any,
      });

      const camposApi = camposVariaveis.map((c) => ({
        ...(c.id != null ? { id: c.id } : {}),
        nome: c.nome,
        obrigatorio: c.obrigatorio,
      }));

      await sincronizarCamposVariaveisModeloAsync({
        modeloTecnicoId: modelo.id,
        payload: { campos: camposApi },
      });

      handleToggleModal();
    } catch {
      // Toasts e erros tratados nos hooks / Axios
    }
  };

  const onError = (errors: any) => {
    console.error("Validation errors  formulario:", errors);
  };

  useEffect(() => {
    if (modelo !== null) {
      reset({
        cliente: modelo.cliente?.nome ?? "N/A",
        produto: produtosParaSelecao.find(
          (p) =>
            normalizarTextoComparacao(p.nome) === normalizarTextoComparacao(modelo.produto?.nome)
        )?.nome,
        nomeModelo: modelo.nome_modelo ?? "N/A",
        posicionamento: capitalize(modelo.posicionamento ?? "N/A") as "Horizontal" | "Vertical",
        tipoFuro: capitalize(modelo.tipo_furo ?? "N/A"),
        temFuro: Boolean(modelo.tem_furo),
        temCargaFotos: Boolean(modelo.tem_carga_foto),
        temDadosVariaveis: Boolean(modelo.tem_dados_variaveis),
        isProvisorio: Boolean(modelo.is_provisorio),
        campoChave: modelo.campo_chave || "",
        observacoes: modelo.observacoes || "",
      });

      setTipoEntregaSelecionado(modelo.tipo_entrega_id ?? 0);

      const tec = tecnologiasParaSelecao.find(
        (t) =>
          normalizarTextoComparacao(t.nome) ===
          normalizarTextoComparacao(modelo?.tecnologia?.nome ?? "")
      );

      setTecnologiasSelecionadas(tec?.id ? [tec.id] : []);

      setFrontFile(modelo.foto_frente_url || null);
      setBackFile(modelo.foto_verso_url || null);
      setCamposVariaveis(
        (modelo.campos_variaveis ?? []).map((c, i) => ({
          rowKey: `db-${c.id}`,
          id: c.id,
          nome: c.nome,
          obrigatorio: Boolean(c.obrigatorio),
          ordem: c.ordem ?? i + 1,
        }))
      );
      setIsChecked(Boolean(modelo.tem_furo));
    }
  }, [modelo, tecnologiasParaSelecao, produtosParaSelecao, reset]);

  return (
    // apenas trocando o título do modal e adaptando a chamada para `onSubmit`
    <div className="fixed inset-0 flex items-center justify-center bg-black/60">
      <div className="h-[600px] w-[840px] space-y-5 overflow-y-auto rounded-xl bg-neutral-50 px-6 py-5 shadow-shape">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IdCard className="text-zinc-800" size={24} />
            <h2 className="text-xl font-semibold text-zinc-800">Edição do Modelo</h2>
          </div>
          <button onClick={handleToggleModal}>
            <X className="size-5 text-zinc-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit as any, onError)} className="space-y-4">
          <div className="flex flex-col gap-4">
            {/* Cliente */}
            <div>
              <p className="mb-2 text-zinc-800">Cliente</p>
              <input
                {...register("cliente")}
                placeholder="Cliente"
                className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
              />
              {errors.cliente && <p className="text-red-500">{errors.cliente.message}</p>}
            </div>

            {/* Produto */}
            <div>
              <p className="text-zinc-800">Produto</p>
              <select
                {...register("produto")}
                className="flex h-12 w-full items-center rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
              >
                <option value="">Selecione</option>

                {produtosParaSelecao.map((produto) => (
                  <option key={produto.id} value={produto.nome}>
                    {produto.nome}
                    {produto.ativo === false ? " (inativo)" : ""}
                  </option>
                ))}
              </select>

              {errors.produto && <p className="text-red-500">{errors.produto.message}</p>}
            </div>

            {/* Nome do Modelo */}
            <div>
              <p className="mb-2 text-zinc-800">Nome do modelo</p>
              <input
                {...register("nomeModelo")}
                placeholder="Nome do Modelo"
                className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
              />
            </div>

            {/* Envio de fotos */}
            <div className="flex w-full flex-col items-center gap-4 rounded-lg border border-gray-300 bg-white p-4 shadow-md">
              <p className="text-lg font-semibold text-zinc-800">Envio de fotos do modelo</p>

              <div className="flex w-full gap-4">
                {/* Upload da Frente */}
                <div className="flex w-1/2 flex-col items-center">
                  <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition-all">
                    <Upload size={20} />
                    <span className="text-sm">Foto Frente</span>
                    <input type="file" onChange={handleFrontFileChange} className="hidden" />
                  </label>

                  {frontFile && (
                    <img
                      src={frontFile}
                      alt="Foto Frente"
                      className="mt-4 max-h-40 w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-100 object-contain shadow-md"
                      onClick={() => setImagemAmpliada(frontFile)}
                    />
                  )}
                </div>

                {/* Upload do Verso */}
                <div className="flex w-1/2 flex-col items-center">
                  <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition-all">
                    <Upload size={20} />
                    <span className="text-sm">Foto Verso</span>
                    <input type="file" onChange={handleBackFileChange} className="hidden" />
                  </label>
                  {backFile && (
                    <img
                      src={backFile}
                      alt="Foto Verso"
                      className="mt-4 max-h-40 w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-100 object-contain shadow-md"
                      onClick={() => setImagemAmpliada(backFile)} //
                    />
                  )}
                </div>
              </div>

              {imagemAmpliada && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
                  <div className="relative">
                    <button
                      className="absolute right-4 top-4 rounded-full bg-white p-2 shadow"
                      onClick={() => setImagemAmpliada(null)}
                    >
                      <X className="text-black" />
                    </button>
                    <img
                      src={imagemAmpliada}
                      alt="Visualização ampliada"
                      className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              )}
            </div>

            {/**TECNOLOGIA */}
            <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-gray-300 bg-white p-4 shadow-md">
              <p className="text-zinc-800">Tipo de tecnologia</p>
              {isLoadingTecnologias ? (
                <div className="flex h-32 w-full items-center justify-center">
                  <SpinnerSimple color="zinc-600" />
                </div>
              ) : (
                <div className="flex w-full flex-row flex-wrap items-center gap-8">
                  {tecnologiasUnicas.map((tec) => (
                    <label key={tec.id} className="flex items-center gap-2 text-zinc-800">
                      <input
                        type="radio"
                        name="tecnologia"
                        checked={tecnologiasSelecionadas[0] === tec.id}
                        onChange={() => setTecnologiasSelecionadas([tec.id])}
                      />
                      {tec.nome}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Checkboxes */}
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-zinc-800">
                <input
                  {...register("temFuro")}
                  type="checkbox"
                  {...register("temFuro")}
                  onChange={(event) => {
                    setValue("temFuro", event.target.checked);
                    setIsChecked(event.target.checked as any);
                  }}
                />
                Tem furo?
              </label>

              <label className="flex items-center gap-2 text-zinc-800">
                <input
                  {...register("temCargaFotos")}
                  type="checkbox"
                  onChange={(e) => setValue("temCargaFotos", e.target.checked)}
                />
                Tem carga de fotos?
              </label>

              <label className="flex items-center gap-2 text-zinc-800">
                <input
                  {...register("temDadosVariaveis")}
                  type="checkbox"
                  onChange={(e) => setValue("temDadosVariaveis", e.target.checked)}
                />
                Tem dados variáveis?
              </label>

              <label className="flex items-center gap-2 text-zinc-800">
                <input
                  {...register("isProvisorio")}
                  type="checkbox"
                  onChange={(e) => setValue("isProvisorio", e.target.checked)}
                />
                Modelo provisório
              </label>
            </div>

            {/* TIPO DE FURO */}
            {isChecked && (
              <div>
                <p className="mb-2 text-zinc-800">Selecione o tipo de furo</p>
                <select
                  {...register("tipoFuro")}
                  className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
                >
                  <option value="">Selecione o tipo de furo</option>
                  <option value="Ovoide">Ovoide</option>
                  <option value="Redondo">Redondo</option>
                </select>
              </div>
            )}

            {/* Posicionamento */}
            <div>
              <p className="mb-2 text-zinc-800">Posicionamento</p>
              <select
                {...register("posicionamento")}
                className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
              >
                <option value="Horizontal">Horizontal</option>
                <option value="Vertical">Vertical</option>
              </select>
            </div>

            {/* Campo Chave */}
            <div>
              <p className="mb-2 text-zinc-800">Campo chave</p>
              <input
                {...register("campoChave")}
                placeholder="Informe o campo chave"
                className="h-12 w-full rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
              />
              {errors.campoChave && <p className="text-red-500">{errors.campoChave.message}</p>}
            </div>
            {/**CAMPOS VARIÁVEIS */}
            <div>
              <p className="mb-2 text-zinc-800">Campos variáveis</p>

              <div className="flex items-center">
                <input
                  placeholder="Informe o nome do campo variável"
                  className="flex h-14 w-10/12 items-center gap-2 rounded-lg border border-zinc-800 px-4 text-zinc-800 outline-none"
                  onChange={(e) => setCampo(e.target.value)}
                  value={campo}
                />

                <Button
                  size="sm"
                  variant="default"
                  className="ml-2 h-14 gap-1 text-neutral-50"
                  type="button"
                  onClick={handleAddCamposVariaveis}
                >
                  <span className="sr-only text-neutral-50 sm:not-sr-only sm:whitespace-nowrap">
                    Adicionar Campo
                  </span>
                </Button>
              </div>

              {messageError.error && <p className="text-red-500">{messageError.message}</p>}

              <label className="mt-2 flex items-center gap-2 text-zinc-800">
                <input type="checkbox" onChange={(e) => setCampoObrigatorio(e.target.checked)} />
                Obrigátorio?
              </label>

              {camposVariaveis?.length > 0 && (
                <table className="mt-4 w-full border-collapse overflow-hidden rounded-lg">
                  <thead className="bg-bg-table-row">
                    <tr className="text-left text-gray-700">
                      <th className="border-b px-4 py-2">#</th>
                      <th className="border-b px-4 py-2">Nome</th>
                      <th className="border-b px-4 py-2">Obrigatório</th>
                      <th className="border-b px-4 py-2 text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {camposVariaveis?.map((item) => (
                      <tr
                        key={item.rowKey}
                        className="text-zinc-800 transition-all hover:bg-gray-100"
                      >
                        <td className="border-b px-4 py-3">{item.ordem}</td>
                        <td className="border-b px-4 py-3">{item.nome}</td>
                        <td className="border-b px-4 py-3">{item.obrigatorio ? "Sim" : "Não"}</td>
                        <td className="border-b px-4 py-3 text-center">
                          <button
                            type="button"
                            className="rounded-md bg-red-500 p-2 text-white transition-all hover:bg-red-600"
                            onClick={() => handleRemoveCamposVariaveis(item.rowKey)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Observações */}
            <div>
              <p className="mb-2 text-zinc-800">Observações</p>
              <textarea
                {...register("observacoes")}
                placeholder="Digite informações adicionais"
                className="h-20 w-full rounded-lg border border-zinc-800 px-4 pt-4 text-zinc-800 outline-none"
              />
            </div>
          </div>

          <div className="flex w-full justify-end">
            <Button disabled={isLoading || isLoadingSyncCampos} type="submit" className="w-full">
              {isLoading || isLoadingSyncCampos ? <SpinnerSimple /> : "Salvar"}
            </Button>
          </div>
        </form>
      </div>
    </div>
    // ...
  );
};

export { EditModeloModal };
